/**
 * AiPanel.jsx
 * Caption generation, sitting inside the composer rather than replacing it.
 *
 * Everything it produces lands in the normal caption fields, where you edit it
 * like anything else. Nothing generated can reach a platform without going
 * through the same approval step as a hand-written post.
 *
 * Image generation was removed in September 2026: models redraw interfaces
 * rather than compositing them, so app screenshots never came out as the real
 * thing. Images are uploaded by hand.
 */

import { useState } from 'react';
import { api } from '../services/api.js';
import { Plus, Warning, Check, Refresh } from './Icons.jsx';

const GOALS = ['awareness', 'signups', 'downloads', 'sales', 'engagement'];

export default function AiPanel({ brand, platforms, onCaptions }) {
  const [topic, setTopic] = useState('');
  const [goal, setGoal] = useState('awareness');
  const [variants, setVariants] = useState(3);
  const [options, setOptions] = useState([]);
  const [chosen, setChosen] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [spend, setSpend] = useState(null);

  const chosenPlatforms = Object.entries(platforms)
    .filter(([, on]) => on)
    .map(([p]) => p);

  async function generateCaptions() {
    setBusy(true);
    setError(null);
    try {
      const res = await api.generate.captions({
        brand_id: brand.id,
        topic,
        goal,
        platforms: chosenPlatforms,
        variants,
      });
      setOptions(res.options);
      setChosen(null);
      setSpend(res.monthToDatePence);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  function use(option, index) {
    setChosen(index);
    onCaptions?.(option);
  }

  return (
    <section className="card">
      <div className="card__head">
        <span className="tile">
          <Plus size={21} />
        </span>
        <div className="card__head-text">
          <h2>Generate captions</h2>
          <p>
            Uses {brand?.name}&apos;s tone, audience and banned claims automatically — you never
            re-explain the brand.
          </p>
        </div>
        {spend != null && (
          <div className="card__head-end">
            <span className="badge">£{(spend / 100).toFixed(2)} this month</span>
          </div>
        )}
      </div>

      {error && (
        <div className="notice notice--error">
          <Warning />
          <span>{error.message}</span>
        </div>
      )}

      <div className="field">
        <label htmlFor="topic">What is this post about?</label>
        <textarea
          id="topic"
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          placeholder="How Archevot breaks an overwhelming week into steps you can actually start"
          style={{ minHeight: 70 }}
        />
      </div>

      <div className="field-row">
        <div className="field">
          <label htmlFor="goal">Goal</label>
          <select id="goal" value={goal} onChange={(e) => setGoal(e.target.value)}>
            {GOALS.map((g) => (
              <option key={g} value={g}>
                {g}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="variants">Options</label>
          <select
            id="variants"
            value={variants}
            onChange={(e) => setVariants(Number(e.target.value))}
          >
            <option value={1}>1 option</option>
            <option value={2}>2 options</option>
            <option value={3}>3 options</option>
          </select>
        </div>
      </div>

      <div className="btn-row">
        <button
          className="btn btn--primary"
          onClick={generateCaptions}
          disabled={busy || topic.trim().length < 3 || chosenPlatforms.length === 0}
        >
          {busy ? 'Writing…' : 'Generate captions'}
        </button>
        {chosenPlatforms.length === 0 && (
          <span className="hint">Choose a platform above first.</span>
        )}
      </div>

      {options.length > 0 && (
        <div className="rows" style={{ marginTop: 18 }}>
          {options.map((o, i) => (
            <div className={`option${chosen === i ? ' is-chosen' : ''}`} key={i}>
              <div className="option__head">
                <strong>Option {i + 1}</strong>
                <button className="btn btn--sm btn--primary" onClick={() => use(o, i)}>
                  {chosen === i ? <Check size={15} /> : null}
                  {chosen === i ? 'Applied' : 'Use this'}
                </button>
              </div>

              {Object.entries(o.captions).map(([platform, text]) => (
                <div className="option__caption" key={platform}>
                  <span className="badge badge--accent">
                    {platform === 'facebook' ? 'Facebook' : 'Instagram'}
                  </span>
                  <p>{text}</p>
                </div>
              ))}

              {o.hashtags && <p className="option__tags">{o.hashtags}</p>}
            </div>
          ))}

          <button className="btn btn--sm" onClick={generateCaptions} disabled={busy}>
            <Refresh size={15} /> Try again
          </button>
        </div>
      )}
    </section>
  );
}
